import { ChevronLeft, ChevronRight, Info } from "lucide-react";

import { Skeleton } from "@components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { cn } from "@utils/ui";

import type { SectionRow } from "../editor/context";

type Props = {
  rows: SectionRow[];
  activeKey: string | null;
  isLoading: boolean;
  onSelect: (key: string) => void;
  /** Collapse is page-controlled so the grid slot can shrink with the rail. */
  collapsed: boolean;
  onToggleCollapse: () => void;
};

export const ModulesPanel = ({
  rows,
  activeKey,
  isLoading,
  onSelect,
  collapsed,
  onToggleCollapse,
}: Props) => {
  if (collapsed) {
    return (
      <CollapsedPanel
        rows={rows}
        activeKey={activeKey}
        onExpand={onToggleCollapse}
        onSelect={onSelect}
      />
    );
  }

  return (
    <aside className="flex h-full min-h-0 w-full flex-col bg-background-white">
      <div className="border-b px-4 pb-3 pt-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Sections</h2>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="text-foreground-muted hover:text-foreground"
            title="Collapse sections"
          >
            <ChevronLeft className="size-4" />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-2">
        {isLoading ? (
          <div className="space-y-2 px-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <ul className="space-y-0.5">
            {rows.map((row) => (
              <li key={row.key}>
                <ModuleListItem
                  row={row}
                  isActive={row.key === activeKey}
                  onClick={() => onSelect(row.key)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
};

const ModuleListItem = ({
  row,
  isActive,
  onClick,
}: {
  row: SectionRow;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left transition-colors",
      // Matches the assessment scribe sections nav (TabLinks): Montserrat at
      // 11px, semibold only while active rather than always-on.
      "font-head text-[11px] text-foreground",
      isActive ? "bg-primary-50 font-semibold text-primary" : "hover:bg-muted/60"
    )}
  >
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="min-w-0 flex-1 truncate">{row.label}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px]">
        {row.label}
      </TooltipContent>
    </Tooltip>
    {row.isCodeOfConduct ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label="About this section"
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 text-foreground-muted hover:text-foreground"
          >
            <Info className="size-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] text-balance">
          These are predefined Code of Conduct acknowledgements, not drawn from your
          mapped notes.
        </TooltipContent>
      </Tooltip>
    ) : null}
    {row.noteCount > 0 ? (
      <span
        className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground"
        title={`${row.noteCount} note${row.noteCount === 1 ? "" : "s"}`}
      >
        {row.noteCount}
      </span>
    ) : null}
    <ChevronRight className="size-4 shrink-0 text-foreground-muted" />
  </button>
);

const CollapsedPanel = ({
  rows,
  activeKey,
  onExpand,
  onSelect,
}: {
  rows: SectionRow[];
  activeKey: string | null;
  onExpand: () => void;
  onSelect: (key: string) => void;
}) => (
  // Collapsed = a narrow labels rail: section names truncated + chevron,
  // header "Sections >" to expand. (Not a numbers/icons rail.)
  <aside className="flex h-full min-h-0 w-full flex-col bg-background-white">
    <div className="flex items-center justify-between border-b px-3 py-4">
      <h2 className="truncate text-sm font-semibold">Sections</h2>
      <button
        type="button"
        onClick={onExpand}
        className="shrink-0 text-foreground-muted hover:text-foreground"
        title="Expand sections"
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
    <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
      {rows.map((row) => {
        const isActive = row.key === activeKey;
        return (
          <li key={row.key}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onSelect(row.key)}
                  className={cn(
                    "flex w-full items-center gap-1 rounded-md px-2 py-2 text-left transition-colors",
                    "font-head text-[11px] text-foreground",
                    isActive
                      ? "bg-primary-50 font-semibold text-primary"
                      : "hover:bg-muted/60"
                  )}
                >
                  {/* Right-edge fade instead of a hard ellipsis — labels dissolve
                      into the rail's narrow edge. */}
                  <span className="min-w-0 flex-1 overflow-hidden whitespace-nowrap [-webkit-mask-image:linear-gradient(to_right,black_70%,transparent)] [mask-image:linear-gradient(to_right,black_70%,transparent)]">
                    {row.label}
                  </span>
                  <ChevronRight className="size-3.5 shrink-0 text-foreground-muted" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[260px]">
                {row.label}
              </TooltipContent>
            </Tooltip>
          </li>
        );
      })}
    </ul>
  </aside>
);
